import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Granularity } from '@/lib/timeSeries';

export interface UsageMetrics {
  assistants: {
    by_type: { tipo: string; sesiones: number; usuarios: number; mensajes: number }[];
    series: { bucket: string; tipo: string; sesiones: number }[];
    depth: { rango: string; sesiones: number }[];
    orgs: { name: string; sesiones: number; usuarios: number }[];
    totals: { sesiones: number; usuarios: number; mensajes: number };
  };
  challenges: {
    series: { bucket: string; retos: number }[];
    solutions_series: { bucket: string; soluciones: number }[];
    by_type: { tipo: string; total: number; convertidas: number }[];
    totals: { retos: number; usuarios: number; soluciones: number; convertidas: number; retos_con_solucion: number };
  };
  content: {
    top: { name: string; category: string; content_type: string; vistas: number; completados: number }[];
    low: { name: string; category: string; vistas: number; completados: number; pct: number | null }[];
    categories: { name: string; vistas: number; completados: number }[];
    types: { name: string; vistas: number }[];
    access: { name: string; vistas: number }[];
    series: { bucket: string; completados: number }[];
    totals: { vistas: number; completados: number; usuarios: number; contenidos: number };
  };
  engagement: {
    dau: number; wau: number; mau: number; total_usuarios: number;
    racha_prom: number; racha_max: number;
  };
  platform: {
    logins_series: { bucket: string; ingresos: number; usuarios: number }[];
    signups_series: { bucket: string; registros: number }[];
    by_hour: { hora: number; total: number }[];
    by_dow: { dia: number; total: number }[];
    matrix: { dia: number; hora: number; total: number }[];
    actions: { action: string; total: number }[];
    frequency: { rango: string; usuarios: number }[];
    inactive: { full_name: string | null; email: string | null; org: string | null; last_login: string | null }[];
    totals: { logins: number; usuarios_login: number; registros: number; eventos: number };
  };
}

export function useExecutiveUsageMetrics(
  from: Date,
  to: Date,
  granularity: Granularity,
  orgId?: string | null,
  enabled = true,
) {
  const fromIso = from.toISOString();
  const toIso = to.toISOString();

  return useQuery({
    queryKey: ['executive-usage-metrics', fromIso, toIso, granularity, orgId ?? null],
    enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<UsageMetrics> => {
      const { data, error } = await supabase.rpc('get_executive_usage_metrics', {
        _from: fromIso,
        _to: toIso,
        _granularity: granularity,
        _org_id: orgId ?? null,
      });
      if (error) throw error;
      return data as unknown as UsageMetrics;
    },
  });
}
